import { useState, useRef, useEffect } from "react";
import useTestWizardConfig from "./useTestWizardConfig";
import useLang from "../../../hooks/useLang";
// import usePostSpectrum from "../../features/hooks/usePostSpectrum";
import { useNavigate } from "react-router-dom";
// import { useLocation } from "react-router-dom";
import { usePostAssessmentMutation } from "../../assesments/assessmentsSlice";
import { useHasAccessFunctions } from "../../../hooks/useHasAccess";
import useOrganisationId from "../../organisations/hooks/useOrganisationId";
import useWizard from "../../../hooks/useWizard";
import { setError } from "../../alerts/alertsSlice";
import { useDispatch } from "react-redux";

const getInitialSpectrum = () => ({
  left: {
    ac: {
      points: [],
    },
    bc: {
      points: [],
    },
    blue_crosses: [],
    blue_slashes: [],
    red_slashes: [],
    red_dbl_slashes: [],
  },
  right: {
    ac: {
      points: [],
    },
    bc: {
      points: [],
    },
    blue_crosses: [],
    blue_slashes: [],
    red_slashes: [],
    red_dbl_slashes: [],
  },
  laterality: [],
});

const useTestPage = () => {
  const lang = useLang();
  const previousStateRef = useRef();
  const previousMarkerRef = useRef();
  const [canUndo, setCanUndo] = useState();
  // const { pathname } = useLocation();
  const {
    steps: flattenedSteps,
    marker,
    handleToStart,
    handleNext,
    handleBack: wizardStepBack,
    handleNextTop,
  } = useWizard({ config: useTestWizardConfig() });
  const [spectrum, setSpectrum] = useState(getInitialSpectrum());
  // eslint-disable-next-line
  const [otherData, setOtherData] = useState({});
  // const [postSpectrum] = usePostSpectrum();
  const [postAssessment] = usePostAssessmentMutation();
  const [loading, setLoading] = useState(false);
  const organisationId = useOrganisationId();
  const navigate = useNavigate();
  const { hasAccessToLink } = useHasAccessFunctions();
  const dispatch = useDispatch();

  const handleQuit = () => {
    setSpectrum(getInitialSpectrum());
    handleToStart();
    setOtherData({});
  };

  const handleSkip = () => {
    if (flattenedSteps[marker]?.type === "selectivity") {
      setSpectrum((state) => {
        const newState = { ...state };
        newState[[flattenedSteps[marker].side]].blue_slashes = null;
        return newState;
      });
    }
    handleNextTop();
    setCanUndo(false);
  };

  const handleBack = () => {
    if (canUndo) {
      setSpectrum(previousStateRef.current);
      if (previousMarkerRef.current !== marker) {
        wizardStepBack();
      }
    }
    setCanUndo(false);
  };

  const handleValueSubmit = ({ type, side, f, l, value, mistake }) => {
    let repeat = mistake && type === "ac" ? true : false;
    setSpectrum((state) => {
      previousStateRef.current = JSON.parse(JSON.stringify(state));
      const newState = { ...state };

      const updateOrPush = (arr, newItem, matchKey = "f") => {
        const index = arr.findIndex(
          (item) => item[matchKey] === newItem[matchKey]
        );

        if (index !== -1) {
          arr[index] = newItem; // overwrite existing
        } else {
          arr.push(newItem); // push new
        }
      };

      if (mistake) {
        if (type === "ac") {
          newState[side][mistake].push({ f, l });
        }
        if (type === "bc") {
          newState[side][mistake].push(f);
          updateOrPush(newState[side][type].points, { f, l });
        }
        if (type === "selectivity") {
          newState[side][mistake].push(f);
        }
      } else if (type !== "selectivity") {
        if (type === "laterality") {
          newState[type].push({ f, v: value });
        } else {
          updateOrPush(newState[side][type].points, { f, l });
        }
      }
      previousMarkerRef.current = marker;
      return newState;
    });
    setCanUndo(true);
    if (!repeat) handleNext();
  };

  const handleChange = (data) => {
    setOtherData((state) => ({
      ...state,
      [`${marker}_${data.id}`]: data.value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const response = await postAssessment({
        params: { id: organisationId },
        body: {
          participant_id: otherData["0_code"],
          spectrum: spectrum,
          notes: {
            general: otherData["0_comment"],
            ac_left: otherData["12_comment"],
            ac_right: otherData["24_comment"],
            bc_left: otherData["33_comment"],
            bc_right: otherData["42_comment"],
            selectivity_left: otherData["selectivity_left"],
            selectivity_right: otherData["selectivity_right"],
            after_test: otherData["70_comment"],
          },
        },
      });
      setLoading(false);
      if (response?.error) {
        dispatch(setError(response?.error?.data));
        navigate(`/${organisationId}/assessments/`);
      } else {
        navigate(
          `/${organisationId}/assessments/${
            response?.data?.assessment_id ? response?.data?.assessment_id : ""
          }`
        );
      }
    } catch (err) {
      dispatch(setError(err));
      setLoading(false);
    }
  };

  useEffect(() => {
    if (previousMarkerRef.current + 1 < marker) {
      setCanUndo(false);
    }
  }, [marker]);

  const page = {
    hasAccess: hasAccessToLink(`/${organisationId}/test`),
    header: {
      title: flattenedSteps[marker || 0]?.title
        ? flattenedSteps[marker || 0]?.title
        : lang(`test.${flattenedSteps[marker || 0]?.id}`),
      centered: true,
      left_buttons:
        flattenedSteps[marker || 0]?.type === "ac" ||
        flattenedSteps[marker || 0]?.type === "bc" ||
        flattenedSteps[marker || 0]?.type === "laterality" ||
        flattenedSteps[marker || 0]?.type === "selectivity" ||
        flattenedSteps[marker || 0]?.type === "instructions"
          ? [
              {
                caption: lang("test.skip"),
                deemphasize: true,
                onClick: handleSkip,
                inline: true,
              },
            ]
          : undefined,
      buttons:
        flattenedSteps[marker || 0]?.type !== "test_landing"
          ? [
              {
                caption: lang("test.quit"),
                deemphasize: true,
                onClick: handleQuit,
                inline: true,
              },
            ]
          : undefined,
    },
    footer:
      flattenedSteps[marker || 0]?.type !== "ac" &&
      flattenedSteps[marker || 0]?.type !== "bc" &&
      flattenedSteps[marker || 0]?.type !== "laterality" &&
      flattenedSteps[marker || 0]?.type !== "selectivity"
        ? {
            buttons: [
              {
                caption:
                  marker === flattenedSteps.length - 1
                    ? lang("test.submit")
                    : marker === 0 ||
                      flattenedSteps[marker || 0]?.type === "instructions"
                    ? lang("test.start")
                    : flattenedSteps[marker || 0]?.lastTest
                    ? lang("test.next")
                    : lang("test.next_test"),
                important: true,
                onClick:
                  marker === flattenedSteps.length - 1
                    ? handleSubmit
                    : handleNext,
                disabled:
                  marker === 0
                    ? otherData["0_code"] == null ||
                      otherData["0_code"] === "undefined"
                    : false,
              },
            ],
            centered: true,
          }
        : undefined,
    ...flattenedSteps[marker || 0],
    handleValueSubmit: (data) => {
      handleValueSubmit({ ...flattenedSteps[marker || 0], ...data });
    },
    handleNext,
    handleBack,
    handleSubmit,
    handleChange,
    lastStep: marker === flattenedSteps.length - 1,
    data: flattenedSteps[marker || 0].side
      ? spectrum[flattenedSteps[marker || 0].side]
      : spectrum,
    loading,
    canUndo,
  };

  return page;
};

export default useTestPage;
